import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { User, Briefcase, ArrowRight } from 'lucide-react';
import type { ReprogramacionHistorial } from '@/components/types/typeCalendar';

interface ReprogramacionTooltipProps {
  historial: ReprogramacionHistorial[];
  children: React.ReactNode;
}

const formatFecha = (fecha: string) =>
  new Date(`${fecha}T00:00:00`).toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
  });

const formatHora = (hora: string) => (hora ? hora.slice(0, 5) : '');

export default function ReprogramacionTooltip({
  historial,
  children,
}: ReprogramacionTooltipProps) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);

  const show = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setCoords({
      top: rect.bottom + 6,
      left: rect.left,
    });
    setOpen(true);
  };

  const hide = () => setOpen(false);

  // Cierra si se hace scroll o se redimensiona, para que no quede "flotando" mal posicionado
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={show}
        onMouseLeave={hide}
        onClick={(e) => {
          // En táctil no hay hover: el primer tap abre, el segundo (fuera) cierra
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        className="flex w-full min-w-0"
      >
        {children}
      </span>

      {open &&
        createPortal(
          <div
            onMouseEnter={show}
            onMouseLeave={hide}
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              zIndex: 100000,
            }}
            className="w-64 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg p-2.5 space-y-2"
          >
            <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide px-0.5">
              Historial de cambios
            </p>
            {historial.map((mov, idx) => {
              const esCliente = mov.reprogramado_por === 'cliente';
              return (
                <div
                  key={`${mov.fecha_creacion}-${idx}`}
                  className="rounded-md bg-gray-50 dark:bg-gray-900/60 px-2 py-1.5"
                >
                  <div className="flex items-center gap-1 mb-1">
                    {esCliente ? (
                      <User className="w-3 h-3 text-blue-500" />
                    ) : (
                      <Briefcase className="w-3 h-3 text-purple-500" />
                    )}
                    <span className="text-[10px] font-semibold text-gray-600 dark:text-gray-300">
                      {esCliente ? 'Cliente' : 'Trabajador'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px]">
                    <span className="text-gray-400 dark:text-gray-500 line-through">
                      {formatFecha(mov.fecha_anterior)}{' '}
                      {formatHora(mov.hora_anterior)}
                    </span>
                    <ArrowRight className="w-3 h-3 text-gray-300 dark:text-gray-600 shrink-0" />
                    <span className="font-medium text-gray-700 dark:text-gray-200">
                      {formatFecha(mov.fecha_nueva)}{' '}
                      {formatHora(mov.hora_nueva)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>,
          document.body
        )}
    </>
  );
}